import React, { useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  X, ExternalLink, ShieldCheck, KeyRound, Mail,
  CheckCircle2, Copy, AlertTriangle, ArrowRight, Loader2,
} from "lucide-react";

/**
 * Guided 3-step Gmail App-Password onboarding modal.
 *
 * Step 1: Enable 2-factor authentication on the Google account.
 * Step 2: Generate a Gmail App Password (16-char code).
 * Step 3: Paste credentials and verify against IMAP — this calls the existing
 *         POST /email/accounts endpoint, which itself does an IMAP login test
 *         before saving (so a 400 here = invalid app password).
 *
 * Renders via React Portal so it always escapes the surrounding `animate-fade-up`
 * containing-block bug fixed for other modals in this app.
 */
export default function GmailAppPasswordModal({ onClose, onAdded }) {
  const [step, setStep] = useState(1);

  return createPortal(
    <div
      className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
      data-testid="gmail-onboarding-overlay"
    >
      <div
        className="brut-card max-w-2xl w-full flex flex-col min-h-0"
        style={{ maxHeight: "calc(100vh - 32px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <Header step={step} onClose={onClose} />
        <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">
          {step === 1 && <Step1Enable2FA onNext={() => setStep(2)} />}
          {step === 2 && <Step2CreatePassword onBack={() => setStep(1)} onNext={() => setStep(3)} />}
          {step === 3 && (
            <Step3VerifyAndSave
              onBack={() => setStep(2)}
              onDone={() => {
                onAdded?.();
                onClose();
              }}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ----------------------------- Layout helpers -----------------------------
function Header({ step, onClose }) {
  return (
    <div className="border-b border-zinc-900 bg-white shrink-0">
      <div className="flex items-center justify-between px-5 py-3">
        <div>
          <div className="label-mono text-zinc-500">CONNECT YOUR GMAIL INBOX</div>
          <h2 className="font-heading text-xl font-black">Gmail App Password setup</h2>
        </div>
        <button
          onClick={onClose}
          className="brut-btn-secondary p-1.5"
          data-testid="gmail-onboarding-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <Steps step={step} />
    </div>
  );
}

function Steps({ step }) {
  const items = [
    { n: 1, label: "Enable 2FA", Icon: ShieldCheck },
    { n: 2, label: "Get App Password", Icon: KeyRound },
    { n: 3, label: "Verify & Save", Icon: Mail },
  ];
  return (
    <div className="flex items-center gap-0 px-5 pb-3" data-testid="gmail-onboarding-steps">
      {items.map((it, idx) => {
        const done = step > it.n;
        const active = step === it.n;
        return (
          <React.Fragment key={it.n}>
            <div
              className={`flex items-center gap-2 px-2 py-1 border ${
                active ? "border-zinc-900 bg-yellow-200" : done ? "border-zinc-900 bg-green-100" : "border-zinc-300 bg-white"
              }`}
              data-testid={`gmail-step-pill-${it.n}`}
            >
              <div className="w-5 h-5 border border-zinc-900 flex items-center justify-center bg-white">
                {done ? <CheckCircle2 className="w-3 h-3" /> : <it.Icon className="w-3 h-3" />}
              </div>
              <span className="label-mono">{it.n}. {it.label}</span>
            </div>
            {idx < items.length - 1 && <div className="w-4 h-px bg-zinc-400 mx-1" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Hint({ tone = "info", children }) {
  const palette = {
    info: "bg-blue-50 border-zinc-900",
    warn: "bg-amber-100 border-zinc-900",
  }[tone];
  const Icon = tone === "warn" ? AlertTriangle : ShieldCheck;
  return (
    <div className={`border ${palette} p-3 flex items-start gap-2 text-sm font-mono`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="leading-relaxed">{children}</div>
    </div>
  );
}

function LinkBtn({ href, children, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-2 brut-btn px-3 py-2 text-xs font-bold"
      data-testid={testid}
    >
      {children} <ExternalLink className="w-3.5 h-3.5" />
    </a>
  );
}

function CopyableUrl({ url, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed — copy manually");
    }
  };
  return (
    <div className="flex items-stretch border border-zinc-900 bg-white">
      <code className="px-2 py-1.5 text-xs font-mono flex-1 truncate" data-testid={testid}>{url}</code>
      <button
        onClick={copy}
        className="border-l border-zinc-900 px-2 hover:bg-zinc-100"
        type="button"
        title="Copy URL"
      >
        {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-green-700" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

// ----------------------------- Step 1 -----------------------------
function Step1Enable2FA({ onNext }) {
  return (
    <div className="space-y-4" data-testid="gmail-step-1">
      <div className="space-y-2">
        <h3 className="font-heading text-lg font-bold">Step 1 — Turn on 2-Step Verification</h3>
        <p className="text-sm text-zinc-700 leading-relaxed">
          Google only lets you create App Passwords once 2-Step Verification is enabled.
          If your account already uses 2FA, skip to the next step.
        </p>
      </div>

      <ol className="list-decimal list-inside text-sm space-y-1.5 font-mono pl-1">
        <li>Open your Google Account 2-Step Verification page.</li>
        <li>Add a phone number or authenticator and confirm.</li>
        <li>Wait for the green "2-Step Verification is ON" banner.</li>
      </ol>

      <CopyableUrl url="https://myaccount.google.com/signinoptions/two-step-verification" testid="step1-url" />

      <Hint>
        <b>Why?</b> App Passwords replace your real Gmail password for IMAP — they're 16-character
        codes that only work for one app and can be revoked any time without touching your main login.
      </Hint>

      <div className="flex items-center justify-between pt-1">
        <LinkBtn
          href="https://myaccount.google.com/signinoptions/two-step-verification"
          testid="step1-open-google"
        >
          OPEN GOOGLE 2-STEP SETUP
        </LinkBtn>
        <button
          onClick={onNext}
          className="brut-btn-secondary px-4 py-2 text-xs font-bold flex items-center gap-1.5"
          data-testid="step1-next"
        >
          NEXT <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ----------------------------- Step 2 -----------------------------
function Step2CreatePassword({ onBack, onNext }) {
  return (
    <div className="space-y-4" data-testid="gmail-step-2">
      <div className="space-y-2">
        <h3 className="font-heading text-lg font-bold">Step 2 — Generate an App Password</h3>
        <p className="text-sm text-zinc-700 leading-relaxed">
          Open the App Passwords page and create a brand-new code labelled
          <code className="font-mono mx-1 bg-zinc-100 border border-zinc-400 px-1">Land Drafters</code>.
          Copy the 16-character code Google shows — that's what you'll paste in step 3.
        </p>
      </div>

      <ol className="list-decimal list-inside text-sm space-y-1.5 font-mono pl-1">
        <li>Open the link below — sign in if prompted.</li>
        <li>App name: <code className="bg-zinc-100 border border-zinc-400 px-1">Land Drafters</code></li>
        <li>Click <b>Create</b>.</li>
        <li>Copy the 16-character code (spaces don't matter — we'll strip them).</li>
        <li>Click <b>Done</b> in Google to dismiss.</li>
      </ol>

      <CopyableUrl url="https://myaccount.google.com/apppasswords" testid="step2-url" />

      <Hint tone="warn">
        Google shows the code <b>only once</b>. If you close the dialog before copying, simply create a
        new one — old codes can be revoked safely.
      </Hint>

      <div className="flex items-center justify-between pt-1">
        <button
          onClick={onBack}
          className="brut-btn-secondary px-4 py-2 text-xs font-bold"
          data-testid="step2-back"
        >
          ← BACK
        </button>
        <div className="flex gap-2">
          <LinkBtn href="https://myaccount.google.com/apppasswords" testid="step2-open-google">
            OPEN APP PASSWORDS
          </LinkBtn>
          <button
            onClick={onNext}
            className="brut-btn px-4 py-2 text-xs font-bold flex items-center gap-1.5"
            data-testid="step2-next"
          >
            I HAVE THE CODE <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------- Step 3 -----------------------------
function Step3VerifyAndSave({ onBack, onDone }) {
  const [form, setForm] = useState({
    email: "",
    app_password: "",
    display_name: "",
    imap_host: "imap.gmail.com",
    imap_port: 993,
  });
  const [advanced, setAdvanced] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const payload = { ...form, app_password: (form.app_password || "").replace(/\s+/g, "") };
    try {
      await api.post("/email/accounts", payload);
      toast.success("Gmail account connected — try syncing now.");
      onDone();
    } catch (err) {
      const detail = err.response?.data?.detail || "Could not verify the credentials";
      setError(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="gmail-step-3">
      <div className="space-y-2">
        <h3 className="font-heading text-lg font-bold">Step 3 — Paste & verify</h3>
        <p className="text-sm text-zinc-700 leading-relaxed">
          Enter the Gmail address and the 16-character App Password from Step 2. We'll
          attempt an IMAP login before saving — if your code is wrong you'll see an error here
          (your real password is never sent).
        </p>
      </div>

      <Field label="GMAIL ADDRESS" required>
        <input
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="you@gmail.com"
          className="brut-input w-full px-3 py-2 text-sm font-mono"
          data-testid="gmail-email-input"
        />
      </Field>

      <Field label="APP PASSWORD (16-CHAR CODE)" required>
        <input
          type="password"
          required
          value={form.app_password}
          onChange={(e) => setForm({ ...form, app_password: e.target.value })}
          placeholder="xxxx xxxx xxxx xxxx"
          autoComplete="off"
          className="brut-input w-full px-3 py-2 text-sm font-mono tracking-widest"
          data-testid="gmail-password-input"
        />
        <div className="label-mono mt-1 text-zinc-500">
          Spaces are removed automatically. Do NOT use your normal Gmail password here.
        </div>
      </Field>

      <Field label="DISPLAY NAME (OPTIONAL)">
        <input
          value={form.display_name}
          onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          placeholder="Team Inbox"
          className="brut-input w-full px-3 py-2 text-sm"
        />
      </Field>

      <button
        type="button"
        onClick={() => setAdvanced((v) => !v)}
        className="text-xs font-mono underline text-zinc-600"
        data-testid="gmail-toggle-advanced"
      >
        {advanced ? "Hide advanced (IMAP host / port)" : "Show advanced (IMAP host / port)"}
      </button>
      {advanced && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="IMAP HOST">
            <input
              value={form.imap_host}
              onChange={(e) => setForm({ ...form, imap_host: e.target.value })}
              className="brut-input w-full px-3 py-2 text-sm font-mono"
            />
          </Field>
          <Field label="PORT">
            <input
              type="number"
              value={form.imap_port}
              onChange={(e) => setForm({ ...form, imap_port: parseInt(e.target.value) || 993 })}
              className="brut-input w-full px-3 py-2 text-sm font-mono"
            />
          </Field>
        </div>
      )}

      {error && (
        <div
          className="border border-red-700 bg-red-50 p-3 text-sm font-mono text-red-900 flex gap-2"
          data-testid="gmail-onboarding-error"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-bold">Could not connect</div>
            <div className="text-xs mt-1">{error}</div>
            <div className="text-xs mt-2 text-zinc-700">
              Most common fixes: (1) make sure 2-Step Verification is ON, (2) regenerate the App
              Password and remove any spaces, (3) confirm the Gmail address matches the account that
              owns the password.
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-1">
        <button
          type="button"
          onClick={onBack}
          className="brut-btn-secondary px-4 py-2 text-xs font-bold"
          data-testid="step3-back"
        >
          ← BACK
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="brut-btn px-4 py-2 text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
          data-testid="gmail-verify-btn"
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          {submitting ? "VERIFYING…" : "VERIFY & CONNECT"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="label-mono mb-1">
        {label}
        {required && <span className="text-red-600"> *</span>}
      </div>
      {children}
    </label>
  );
}
